import type {
  ActionItemProps,
  ActionsProps,
} from './division-form-actions.interface';
import type { FieldArrayRenderProps } from 'formik';
import type { FC } from 'react';

import {
  closestCenter,
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FieldArray } from 'formik';
import { confirmAlert } from 'react-confirm-alert';

import { usePermissions } from '../../../../contexts/permissions.context';
import * as utils from '../../../../shared/utils';
import { vocabulary } from '../../../../vocabulary/german';
import { CreateButton } from '../../../buttons/create-button/create-button';
import { SelectField } from '../../../fields/select-field/select-field';
import { TextareaField } from '../../../fields/textarea-field/textarea-field';
import { ConfirmationModal } from '../../../template/modals/confirmation-modal';

const ActionItem = ({
  action,
  actionIndex,
  shortTermGoalIndex,
  actionHelpers,
}: ActionItemProps) => {
  const matrix = usePermissions();

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id: action.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : 'transform 500ms ease',
  };

  return (
    <div
      className="division-actions__wrapper"
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <SelectField
        name={`ShortTermGoals.${shortTermGoalIndex}.Actions.${actionIndex}.status`}
        className="division-actions__status customized"
        forDivision
        value={action.status}
      >
        <option value={0} label={vocabulary.statuses.notStarted} />
        <option value={1} label={vocabulary.statuses.inProgress} />
        <option value={2} label={vocabulary.statuses.finished} />
      </SelectField>
      <div className="field-wrapper">
        <TextareaField
          forDivision
          name={`ShortTermGoals.${shortTermGoalIndex}.Actions.${actionIndex}.title`}
          placeholder="Massnahme"
        />
        {matrix.permissions.removeFieldsInside === true && (
          <i
            className={'division__remove-icon'}
            onClick={() => {
              confirmAlert({
                customUI: ({ onClose }) => {
                  return (
                    <ConfirmationModal
                      forDivision
                      onClose={onClose}
                      onOk={() => actionHelpers.remove(actionIndex)}
                    />
                  );
                },
              });
            }}
          />
        )}
      </div>
      {matrix.permissions.dragFields === true && (
        <span
          className="priorities__sort-handle"
          {...listeners}
          style={{
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          ::
        </span>
      )}
    </div>
  );
};

export const DivisionFormActions: FC<ActionsProps> = ({
  shortTermGoal,
  shortTermGoalIndex,
  currentYearFrozen = false,
}: ActionsProps) => {
  const matrix = usePermissions();
  const addAction = (helpers: FieldArrayRenderProps) => () => {
    helpers.push({
      title: '',
      YearId: utils.getCurrentYearIdFromLS(),
      status: 0,
      order: (shortTermGoal.Actions?.length || 0) + 1,
    });
  };

  const isTouchDevice =
    typeof window !== 'undefined' && 'ontouchstart' in window;
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 200, tolerance: 5 },
  });

  const sensors = useSensors(isTouchDevice ? touchSensor : pointerSensor);
  return (
    <FieldArray
      name={`ShortTermGoals.${shortTermGoalIndex}.Actions`}
      render={(actionHelpers) => {
        const hasActions =
          shortTermGoal.Actions && shortTermGoal.Actions.length > 0;

        const handleDragOver = (event: any) => {
          const { active, over } = event;
          if (active.id !== over?.id) {
            const oldIndex = (shortTermGoal.Actions || []).findIndex(
              (item) => active.id === item.id,
            );
            const newIndex = (shortTermGoal.Actions || []).findIndex(
              (item) => over.id === item.id,
            );
            actionHelpers.move(oldIndex, newIndex);
          }
        };

        const handleDragEnd = () => {
          if (hasActions) {
            (shortTermGoal.Actions || []).forEach((_, index) => {
              actionHelpers.form.setFieldValue(
                `${actionHelpers.name}[${index}].order`,
                index + 1,
              );
            });
          }
        };

        return (
          <div className={'division-actions'}>
            {hasActions ? (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={shortTermGoal.Actions || []}
                    strategy={verticalListSortingStrategy}
                  >
                    {(shortTermGoal.Actions || []).map(
                      (action, actionIndex) => (
                        <ActionItem
                          key={action.id}
                          action={action}
                          actionIndex={actionIndex}
                          actionHelpers={actionHelpers}
                          shortTermGoalIndex={shortTermGoalIndex}
                        />
                      ),
                    )}
                  </SortableContext>
                </DndContext>
                {currentYearFrozen
                  ? matrix.permissions.freezeAddFieldsInside === true
                  : matrix.permissions.addFieldsInside === true && (
                      <CreateButton
                        text={vocabulary.buttons.addAction}
                        onClick={addAction(actionHelpers)}
                      />
                    )}
              </>
            ) : (
              <>
                {currentYearFrozen
                  ? matrix.permissions.freezeAddFieldsInside === true
                  : matrix.permissions.addFieldsInside === true && (
                      <CreateButton
                        text={vocabulary.buttons.addAction}
                        onClick={addAction(actionHelpers)}
                      />
                    )}
              </>
            )}
          </div>
        );
      }}
    />
  );
};
